import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@/context/ThemeContext";

interface GuideScreen {
  tag: string;
  title: string;
  description: string;
  tags: string[];
  image: string;
  lightImage: string;
  align?: "left" | "center";
}

export default function GuidePage() {
  const { t } = useTranslation("home");
  const navigate = useNavigate();
  const { effectiveTheme } = useTheme();
  const isLight = effectiveTheme === "light";
  const [current, setCurrent] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState(1);
  const touchStartY = useRef(0);
  const wheelAccum = useRef(0);

  const screens: GuideScreen[] = [
    {
      tag: t("scenarios.item1.tag"),
      title: t("scenarios.item1.title"),
      description: t("scenarios.item1.desc"),
      tags: t("scenarios.item1.tags").split(","),
      image:
        "/images/misc/guide_1.jpg",
      lightImage:
        "/images/misc/guide_1_light.jpg",
      align: "left",
    },
    {
      tag: t("scenarios.item2.tag"),
      title: t("scenarios.item2.title"),
      description: t("scenarios.item2.desc"),
      tags: t("scenarios.item2.tags").split(","),
      image:
        "/images/misc/guide_2.jpg",
      lightImage:
        "/images/misc/guide_2_light.jpg",
      align: "left",
    },
    {
      tag: t("scenarios.item3.tag"),
      title: t("scenarios.item3.title"),
      description: t("scenarios.item3.desc"),
      tags: t("scenarios.item3.tags").split(","),
      image:
        "/images/misc/guide_3.jpg",
      lightImage:
        "/images/misc/guide_3_light.jpg",
      align: "left",
    },
    {
      tag: t("about.brand"),
      title: t("guide.title"),
      description: t("guide.description"),
      tags: [],
      image:
        "/images/misc/guide_4.jpg",
      lightImage:
        "/images/misc/guide_4_light.jpg",
      align: "center",
    },
  ];

  const goTo = useCallback(
    (index: number) => {
      if (
        isAnimating ||
        index === current ||
        index < 0 ||
        index >= screens.length
      ) {
        return;
      }
      setDirection(index > current ? 1 : -1);
      setIsAnimating(true);
      setCurrent(index);
      setTimeout(() => setIsAnimating(false), 850);
    },
    [current, isAnimating, screens.length],
  );

  // Wheel scroll
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (isAnimating) return;
      wheelAccum.current += e.deltaY;
      const threshold = 80;
      if (wheelAccum.current > threshold) {
        wheelAccum.current = 0;
        goTo(current + 1);
      } else if (wheelAccum.current < -threshold) {
        wheelAccum.current = 0;
        goTo(current - 1);
      }
    };
    window.addEventListener("wheel", handleWheel, { passive: true });
    return () => window.removeEventListener("wheel", handleWheel);
  }, [current, isAnimating, goTo]);

  // Touch swipe
  useEffect(() => {
    const handleTouchStart = (e: TouchEvent) => {
      touchStartY.current = e.touches[0].clientY;
    };
    const handleTouchEnd = (e: TouchEvent) => {
      if (isAnimating) return;
      const diff = touchStartY.current - e.changedTouches[0].clientY;
      if (diff > 80) goTo(current + 1);
      else if (diff < -80) goTo(current - 1);
    };
    window.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchend", handleTouchEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [current, isAnimating, goTo]);

  // Keyboard
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (isAnimating) return;
      if (e.key === "ArrowDown" || e.key === "PageDown" || e.key === " ") {
        e.preventDefault();
        goTo(current + 1);
      } else if (e.key === "ArrowUp" || e.key === "PageUp") {
        e.preventDefault();
        goTo(current - 1);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [current, isAnimating, goTo]);

  const slideVariants = {
    enter: (dir: number) => ({
      y: dir > 0 ? "100%" : "-100%",
      opacity: 0,
    }),
    center: {
      y: 0,
      opacity: 1,
    },
    exit: (dir: number) => ({
      y: dir > 0 ? "-100%" : "100%",
      opacity: 0,
    }),
  };

  const screen = screens[current];
  const isLast = current === screens.length - 1;

  return (
    <div
      className={`relative h-screen w-full overflow-hidden ${
        isLight ? "bg-[#f5f5f7]" : "bg-cosmic-950"
      }`}
    >
      <AnimatePresence initial={false} custom={direction} mode="popLayout">
        <motion.div
          key={current}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.75, ease: [0.32, 0.72, 0, 1] }}
          className="absolute inset-0"
        >
          {/* Background Image */}
          <div className="absolute inset-0">
            <img
              src={isLight ? screen.lightImage : screen.image}
              alt={screen.tag}
              className="h-full w-full object-cover"
            />
            <div
              className={`absolute inset-0 bg-gradient-to-t ${
                isLight
                  ? "from-[#f5f5f7] via-[#f5f5f7]/70 to-[#f5f5f7]/40"
                  : "from-cosmic-950 via-cosmic-950/70 to-cosmic-950/40"
              }`}
            />
            <div
              className={`absolute inset-0 ${isLight ? "bg-black/5" : "bg-black/10"}`}
            />
          </div>

          {/* Content */}
          <div className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-end px-6 pb-24 sm:px-16 sm:pb-32 md:px-24">
            {screen.align === "center" ? (
              <div className="mb-12 text-center sm:mb-20">
                <motion.span
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35, duration: 0.6 }}
                  className={`text-xs font-semibold uppercase tracking-[0.2em] sm:text-sm ${
                    isLight ? "text-[#2d2d2d]" : "text-gold-500"
                  }`}
                >
                  {screen.tag}
                </motion.span>
                <motion.h1
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.45, duration: 0.7 }}
                  className={`mt-4 text-4xl font-bold leading-tight tracking-tight sm:text-5xl md:text-6xl lg:text-7xl whitespace-pre-line ${
                    isLight ? "text-[#1c1c1e]" : "text-white"
                  }`}
                >
                  {screen.title}
                </motion.h1>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, duration: 0.6 }}
                  className={`mt-6 text-lg font-light sm:text-xl md:text-2xl ${
                    isLight ? "text-[#6e6e73]" : "text-silver-400"
                  }`}
                >
                  {screen.description}
                </motion.p>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.75, duration: 0.6 }}
                  className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center"
                >
                  <button
                    onClick={() => navigate("/")}
                    className={`rounded-full border px-8 py-3.5 text-sm font-medium tracking-wide transition-colors duration-300 whitespace-nowrap ${
                      isLight
                        ? "border-[#2d2d2d]/50 bg-[#4a4a4c]/15 text-[#2d2d2d] hover:bg-[#4a4a4c]/30"
                        : "border-gold-500/50 bg-gold-500/15 text-gold-500 hover:bg-gold-500/30"
                    }`}
                  >
                    {t("guide.enter_home")}
                  </button>
                  <button
                    onClick={() => navigate("/contact")}
                    className={`rounded-full border px-8 py-3.5 text-sm font-medium tracking-wide transition-colors duration-300 whitespace-nowrap ${
                      isLight
                        ? "border-black/20 bg-black/5 text-[#6e6e73] hover:bg-black/10"
                        : "border-white/20 bg-white/5 text-silver-300 hover:bg-white/10"
                    }`}
                  >
                    {t("guide.contact_us")}
                  </button>
                </motion.div>
              </div>
            ) : (
              <div className="max-w-2xl">
                <motion.span
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35, duration: 0.6 }}
                  className={`text-xs font-semibold uppercase tracking-[0.2em] sm:text-sm ${
                    isLight ? "text-[#2d2d2d]" : "text-gold-500"
                  }`}
                >
                  {screen.tag}
                </motion.span>
                <motion.h2
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.45, duration: 0.7 }}
                  className={`mt-4 text-3xl font-bold leading-tight tracking-tight sm:text-4xl md:text-5xl lg:text-6xl whitespace-pre-line ${
                    isLight ? "text-[#1c1c1e]" : "text-white"
                  }`}
                >
                  {screen.title}
                </motion.h2>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, duration: 0.6 }}
                  className={`mt-5 text-base font-light leading-relaxed sm:text-lg ${
                    isLight ? "text-[#6e6e73]" : "text-silver-400"
                  }`}
                >
                  {screen.description}
                </motion.p>
                {screen.tags.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.75, duration: 0.5 }}
                    className={`mt-8 flex flex-wrap items-center gap-2 text-xs sm:text-sm ${
                      isLight ? "text-[#6e6e73]" : "text-silver-500"
                    }`}
                  >
                    {screen.tags.map((tag, i) => (
                      <span key={i} className="flex items-center whitespace-nowrap">
                        {i > 0 && (
                          <span
                            className={`mr-2 ${
                              isLight ? "text-[#6e6e73]/50" : "text-silver-500/50"
                            }`}
                          >
                            ·
                          </span>
                        )}
                        {tag}
                      </span>
                    ))}
                  </motion.div>
                )}
              </div>
            )}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Top Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 py-5 sm:px-10">
        <button
          onClick={() => navigate("/")}
          className={`flex items-center gap-2 text-sm transition-colors cursor-pointer ${
            isLight
              ? "text-[#6e6e73] hover:text-[#1c1c1e]"
              : "text-silver-400 hover:text-white"
          }`}
        >
          <span className="flex h-5 w-5 items-center justify-center">
            <i className="ri-arrow-left-line" />
          </span>
          <span>{t("guide.skip")}</span>
        </button>
        <span
          className={`font-mono text-xs tracking-wider ${
            isLight ? "text-[#6e6e73]" : "text-silver-500"
          }`}
        >
          {String(current + 1).padStart(2, "0")} /{" "}
          {String(screens.length).padStart(2, "0")}
        </span>
      </div>

      {/* Right Nav Dots */}
      <div className="fixed top-1/2 right-6 z-50 flex -translate-y-1/2 flex-col items-center gap-3 sm:right-10">
        {screens.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`rounded-full transition-all duration-500 cursor-pointer ${
              i === current
                ? `h-6 w-2 ${isLight ? "bg-[#4a4a4c]" : "bg-gold-500"}`
                : isLight
                ? "h-2 w-2 bg-black/20 hover:bg-black/40"
                : "h-2 w-2 bg-white/30 hover:bg-white/50"
            }`}
            aria-label={t("guide.screen", { count: i + 1 })}
          />
        ))}
      </div>

      {/* Scroll Hint */}
      {!isLast && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 flex-col items-center gap-1"
        >
          <span
            className={`text-[10px] uppercase tracking-widest ${
              isLight ? "text-[#6e6e73]" : "text-silver-500"
            }`}
          >
            {t("guide.scroll")}
          </span>
          <motion.div
            animate={{ y: [0, 6, 0] }}
            transition={{
              repeat: Infinity,
              duration: 1.5,
              ease: "easeInOut",
            }}
            className={`flex h-5 w-5 items-center justify-center ${
              isLight ? "text-[#6e6e73]" : "text-silver-500"
            }`}
          >
            <i className="ri-arrow-down-line" />
          </motion.div>
        </motion.div>
      )}

      {/* Bottom Progress Bar */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-50 h-[2px] ${
          isLight ? "bg-black/10" : "bg-white/10"
        }`}
      >
        <motion.div
          className={`h-full ${isLight ? "bg-[#4a4a4c]/60" : "bg-gold-500/60"}`}
          initial={false}
          animate={{ width: `${((current + 1) / screens.length) * 100}%` }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        />
      </div>
    </div>
  );
}